import { providers } from '../ai/registry.js';
import { triggeredOverrideMs, startStatus } from '../state/app-state.js';
import type { VisionLabel } from '../ai/provider.js';

export interface CameraHandlers {
  onClassified: (label: VisionLabel) => void;
}

export function renderCameraPanel(host: HTMLElement, h: CameraHandlers): void {
  host.innerHTML = `
    <div class="panel">
      <h2>Camera</h2>
      <div class="camera-row">
        <video id="cam-video" class="camera-preview" muted playsinline></video>
        <div class="camera-buttons">
          <button id="cam-enable" class="button">Enable camera</button>
          <button id="cam-capture" class="button" disabled>Camera Input</button>
          <label>
            <span>Triggered display (ms)</span>
            <input id="cam-display" type="number" min="2000" max="60000" step="1000" value="20000" />
          </label>
          <div id="cam-status"></div>
        </div>
      </div>
    </div>`;
  const video = host.querySelector<HTMLVideoElement>('#cam-video')!;
  const enable = host.querySelector<HTMLButtonElement>('#cam-enable')!;
  const capture = host.querySelector<HTMLButtonElement>('#cam-capture')!;
  const display = host.querySelector<HTMLInputElement>('#cam-display')!;
  const status = host.querySelector<HTMLDivElement>('#cam-status')!;

  display.addEventListener('change', () => triggeredOverrideMs.set(Number(display.value)));

  let activeStream: MediaStream | null = null;

  function stopCamera(): void {
    if (activeStream) {
      for (const track of activeStream.getTracks()) track.stop();
      activeStream = null;
    }
    video.pause();
    video.srcObject = null;
    capture.disabled = true;
    enable.textContent = 'Enable camera';
    status.textContent = 'Camera disabled.';
    // Clearing any active camera-triggered scene so the rotation returns to defaults.
    h.onClassified('NONE');
  }

  async function startCamera(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      activeStream = stream;
      video.srcObject = stream;
      await video.play();
      capture.disabled = false;
      enable.textContent = 'Disable camera';
      status.textContent = 'Camera enabled.';
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : 'Camera unavailable';
      activeStream = null;
    }
  }

  enable.addEventListener('click', () => {
    if (activeStream) stopCamera();
    else void startCamera();
  });

  capture.addEventListener('click', async () => {
    if (!video.videoWidth) return;
    capture.disabled = true; status.textContent = 'Classifying…';
    const done = startStatus('Classifying camera image');
    try {
      const canvas = document.createElement('canvas');
      const w = 512, ch = Math.round(512 * video.videoHeight / video.videoWidth);
      canvas.width = w; canvas.height = ch;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(video, 0, 0, w, ch);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const base64 = dataUrl.split(',')[1] ?? '';
      const label = await providers.vision.classifyPeople(base64);
      status.textContent = `Result: ${label}`;
      h.onClassified(label);
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : 'Classify failed';
    } finally {
      done();
      capture.disabled = false;
    }
  });
}
